import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "react-query";
import { fetchActiveVideoRooms } from "../api/client";
import { useSocketContext } from "../context/SocketContext";
import { useAuthContext } from "../context/AuthContext";
import { getActiveWorkspaceSlug } from "../utils/workspaceStorage";
import { ACTIVE_VIDEO_ROOMS_KEY } from "../utils/activeVideoUi";

const POLL_MS = 20_000;

function normalizeRooms(rooms) {
  return (Array.isArray(rooms) ? rooms : [])
    .map((room) => ({
      id: Number(room.id),
      context: room.context === "agenda"
        ? "agenda"
        : room.context === "team"
          ? "team"
          : room.context === "vo_private"
            ? "vo_private"
            : room.context === "vo_huddle"
              ? "vo_huddle"
            : room.context === "virtual_office"
              ? "virtual_office"
              : "support",
      teamSpaceId:
        Number.isFinite(Number(room.teamSpaceId)) && Number(room.teamSpaceId) > 0
          ? Number(room.teamSpaceId)
          : null,
      agendaEventId:
        Number.isFinite(Number(room.agendaEventId)) && Number(room.agendaEventId) > 0
          ? Number(room.agendaEventId)
          : null,
      title: String(room.title || "").trim(),
      status: room.status,
      isPrivate: Boolean(room.isPrivate),
      canJoin: Boolean(room.canJoin),
      joinUrl: room.canJoin ? room.agentJoinUrl || room.joinUrl || null : null,
      agentJoinUrl: room.canJoin ? room.agentJoinUrl || room.joinUrl || null : null,
      startedAt: room.startedAt || null,
      participantsInCall: Array.isArray(room.participantsInCall)
        ? room.participantsInCall
            .map((p) => ({
              identity: String(p?.identity || "").trim(),
              sid: p?.sid || null,
              role: p?.role || null,
              displayName: String(p?.displayName || "").trim() || null,
              avatarUrl: p?.avatarUrl || null,
            }))
            .filter((p) => p.identity)
        : [],
      participantCount: Number.isFinite(Number(room.participantCount))
        ? Number(room.participantCount)
        : Array.isArray(room.participantsInCall)
          ? room.participantsInCall.length
          : 0,
    }))
    .filter((room) => Number.isFinite(room.id) && room.id > 0);
}

function isTransientAuthFailure(error) {
  const status = error?.response?.status ?? error?.status;
  const code = error?.response?.data?.code ?? error?.code;
  return (
    status === 503 ||
    code === "errors.auth.temporarily_unavailable" ||
    code === "errors.auth.forbidden"
  );
}

export function useActiveVideoRooms({ enabled = true } = {}) {
  const queryClient = useQueryClient();
  const { socket } = useSocketContext();
  const { bearerToken } = useAuthContext() || {};
  const tenantSlug = getActiveWorkspaceSlug();
  const canFetch = Boolean(enabled && bearerToken && tenantSlug);

  const query = useQuery(
    ACTIVE_VIDEO_ROOMS_KEY,
    async () => normalizeRooms(await fetchActiveVideoRooms()),
    {
      enabled: canFetch,
      staleTime: 10_000,
      refetchInterval: canFetch ? POLL_MS : false,
      refetchOnWindowFocus: true,
      keepPreviousData: true,
      retry: (failureCount, error) => {
        if (!isTransientAuthFailure(error)) return failureCount < 1;
        return failureCount < 3;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
    }
  );

  useEffect(() => {
    if (!socket || !canFetch) return undefined;

    const invalidate = () => {
      queryClient.invalidateQueries(ACTIVE_VIDEO_ROOMS_KEY);
    };

    socket.on("video_room", invalidate);
    socket.on("team_message", invalidate);

    return () => {
      socket.off("video_room", invalidate);
      socket.off("team_message", invalidate);
    };
  }, [socket, queryClient, canFetch]);

  const rooms = useMemo(() => normalizeRooms(query.data), [query.data]);

  const byTeamSpaceId = useMemo(() => {
    const map = new Map();
    for (const room of rooms) {
      if (room.teamSpaceId) map.set(room.teamSpaceId, room);
    }
    return map;
  }, [rooms]);

  const byAgendaEventId = useMemo(() => {
    const map = new Map();
    for (const room of rooms) {
      if (room.agendaEventId) map.set(room.agendaEventId, room);
    }
    return map;
  }, [rooms]);

  return {
    rooms,
    byTeamSpaceId,
    byAgendaEventId,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}

export function invalidateActiveVideoRooms(queryClient) {
  queryClient.invalidateQueries(ACTIVE_VIDEO_ROOMS_KEY);
}
